// Live-Modus einer Geräteseite: Punkte 2 bis 4 aus docs/design/live-verlauf.md.
//
// Solange jemand hinsieht, fragt der Agent dieses eine Gerät alle fünf
// Sekunden ab. Die Beobachtung läuft von selbst aus — die Seite verlängert
// sie, solange sie offen ist, und beendet sie beim Verlassen. Ein zugeklappter
// Laptop hört damit auf, das Kundennetz zu belasten, ohne dass jemand daran
// denken muss.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiSend } from './api'
import type { LiveSession } from './types'

/** Abstand, in dem die offene Seite die Beobachtung verlängert. */
export const LIVE_KEEPALIVE_MS = 60_000

/** Sekunden bis zum Ende, nie negativ. */
export function restlaufzeit(expiresAt: string | undefined, now: number): number {
  if (!expiresAt) return 0
  const ende = new Date(expiresAt).getTime()
  if (Number.isNaN(ende)) return 0
  return Math.max(0, Math.round((ende - now) / 1000))
}

/** "mm:ss" – die Anzeige aus dem Entwurf: „noch 09:41". */
export function formatRestlaufzeit(seconds: number): string {
  const minuten = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${String(minuten).padStart(2, '0')}:${String(rest).padStart(2, '0')}`
}

/** Der vollständige Text neben dem Knopf. */
export function liveHinweis(intervalS: number, verbleibend: number): string {
  return `Live · alle ${intervalS} s · noch ${formatRestlaufzeit(verbleibend)}`
}

export interface LiveModus {
  /** Läuft gerade eine Beobachtung dieses Geräts? */
  aktiv: boolean
  /** Abfrageintervall des Agents, solange sie läuft. */
  intervalS: number
  /** Sekunden bis zum selbsttätigen Ende. */
  verbleibend: number
  belegt: number
  grenze: number
  /** Meldung, wenn das Starten nicht ging (etwa: alle Plätze belegt). */
  fehler: string | null
  startet: boolean
  start: () => void
  beenden: () => void
}

/**
 * Hält die Beobachtung eines Geräts.
 *
 * `erlaubt` ist das Recht `live.start`: ohne es wird gar nicht erst gefragt,
 * damit die Seite keine 403 produziert.
 */
export function useLiveModus(deviceId: string, erlaubt: boolean): LiveModus {
  const queryClient = useQueryClient()
  const [fehler, setFehler] = useState<string | null>(null)
  const [jetzt, setJetzt] = useState(() => Date.now())

  const stand = useQuery({
    queryKey: ['live', deviceId],
    queryFn: ({ signal }) => apiGet<LiveSession>(`/devices/${deviceId}/live`, signal),
    enabled: erlaubt,
  })

  const merken = useCallback(
    (session: LiveSession) => {
      queryClient.setQueryData(['live', deviceId], session)
      setJetzt(Date.now())
    },
    [deviceId, queryClient],
  )

  const start = useMutation({
    mutationFn: () => apiSend<LiveSession>('POST', `/devices/${deviceId}/live`),
    onSuccess: (session) => {
      setFehler(null)
      merken(session)
    },
    onError: (error: unknown) => {
      setFehler(
        error instanceof ApiError
          ? error.message
          : 'Die Live-Beobachtung kam nicht zustande.',
      )
    },
  })

  const beenden = useMutation({
    mutationFn: () => apiSend<LiveSession>('DELETE', `/devices/${deviceId}/live`),
    onSuccess: (session) => {
      setFehler(null)
      merken({ ...session, active: false })
    },
  })

  const aktiv = (stand.data?.active ?? false) && restlaufzeit(stand.data?.expires_at, jetzt) > 0

  // Ein Zeiger für die Anzeige „noch mm:ss". Er läuft nur, solange etwas
  // läuft: ein Intervall im Leerlauf weckt den Browser ohne Grund.
  useEffect(() => {
    if (!aktiv) return
    const id = setInterval(() => setJetzt(Date.now()), 1000)
    return () => clearInterval(id)
  }, [aktiv])

  // Verlängern, solange die Seite offen ist. Derselbe Aufruf wie zum
  // Starten: die Zentrale schiebt das Ende nach hinten.
  const verlaengern = useRef(start.mutate)
  verlaengern.current = start.mutate
  useEffect(() => {
    if (!aktiv) return
    const id = setInterval(() => verlaengern.current(), LIVE_KEEPALIVE_MS)
    return () => clearInterval(id)
  }, [aktiv])

  // Beim Verlassen der Seite beenden. Ohne das liefe die Beobachtung noch
  // bis zu zehn Minuten weiter und belegte einen Platz des Agents.
  const laeuft = useRef(false)
  laeuft.current = aktiv
  useEffect(() => {
    return () => {
      if (!laeuft.current) return
      void apiSend('DELETE', `/devices/${deviceId}/live`).catch(() => {
        // Die Zentrale beendet sie ohnehin, wenn niemand mehr verlängert.
      })
    }
  }, [deviceId])

  const session = stand.data
  return {
    aktiv,
    intervalS: session?.interval_s ?? 5,
    verbleibend: restlaufzeit(session?.expires_at, jetzt),
    belegt: session?.slots_used ?? 0,
    grenze: session?.slots_limit ?? 0,
    fehler,
    startet: start.isPending,
    start: () => start.mutate(),
    beenden: () => beenden.mutate(),
  }
}

/**
 * Hält die letzten Messpunkte je Schlüssel im Browser fest.
 *
 * Die Interface-Tabelle zeigt einen Mini-Verlauf je Port (Punkt 5). Ihn je
 * Port einzeln aus dem Verlauf zu holen, wären bei zwanzig Ports zwanzig
 * Abfragen alle fünf Sekunden. Die Momentaufnahme, die die Seite ohnehin
 * holt, enthält alle Ports — sie wird hier einfach mitgeschrieben.
 *
 * `stempel` ist der Zeitpunkt der Momentaufnahme: nur ein neuer Zeitpunkt
 * hängt einen Punkt an, ein erneutes Zeichnen derselben Daten nicht.
 */
export function useMitschnitt(
  punkte: Record<string, number>,
  stempel: string | undefined,
  laenge = 60,
): Record<string, number[]> {
  const [verlauf, setVerlauf] = useState<Record<string, number[]>>({})
  const letzter = useRef<string | undefined>(undefined)
  const aktuell = useRef(punkte)
  aktuell.current = punkte

  useEffect(() => {
    if (!stempel || stempel === letzter.current) return
    letzter.current = stempel
    setVerlauf((bisher) => {
      const naechster: Record<string, number[]> = {}
      for (const [key, wert] of Object.entries(aktuell.current)) {
        naechster[key] = [...(bisher[key] ?? []), wert].slice(-laenge)
      }
      return naechster
    })
  }, [stempel, laenge])

  return verlauf
}
